// Karta „Zwróć uwagę” (kurs v2, po scence, tylko w lekcjach, które wprowadzają nowy wzorzec; SPEC §22.2, przebudowana w patch-50 wg makiety „Poprawka – gramatyka”):
// tytuł, krótkie wyjaśnienie (każde zdanie w osobnej linijce), dwa przykłady w dymkach z zaimkiem po lewej i pogrubionym (zakreślonym) wzorcem, karta „Wzorzec”,
// karta „Typowy błąd” (✗ / ✓), wskazówka z żarówką i „Szybkie sprawdzenie” z dwiema odpowiedziami obok siebie. „Dalej” stoi pod treścią (nie przykleja się nad nią).
// Sprawdzenie nie liczy się do wyniku — nic się tu nie ocenia. Bez maskotki.
import type { GrammarTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h, shake, trackPlSlot } from './dom.ts';
import { tx, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { fitToWindow, icon, translateButton } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  chip: { en: 'Pay attention', pl: 'Zwróć uwagę' },
  formula: { en: 'Pattern', pl: 'Wzorzec' },
  mistake: { en: 'Common mistake', pl: 'Typowy błąd' },
  check: { en: 'Quick check', pl: 'Szybkie sprawdzenie' },
} as const satisfies Record<string, Pair>;

/** Pierwszy zaimek osoby w zdaniu przykładu (chip po lewej dymka; „Look, that’s Ben” → That); `This` / `That` też, bo wzorzec „This is…” stoi na nich. Bez zaimka — numer przykładu. */
const SUBJECT = /\b(I|we|you|he|she|it|they|this|that)\b/i;
export function subjectChip(text: string, index: number): string {
  const m = SUBJECT.exec(text.trim());
  if (!m) return String(index + 1);
  const w = m[1]!;
  return w.toLowerCase() === 'i' ? 'I' : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/** Wyjaśnienie reguły w osobnych linijkach: jedno zdanie = jedna linijka (granica: kropka, spacja, wielka litera albo cudzysłów). */
export function ruleLines(rule: string): string[] {
  return rule.split(/(?<=[.!?])\s+(?=[A-ZĄĆĘŁŃÓŚŹŻ„"])/u).map((x) => x.trim()).filter(Boolean);
}

export function renderGrammar(task: GrammarTask, env: TaskEnv): TaskView {
  const { ctx } = env;
  const { card } = task;
  const { note } = card;

  const examples = h('div', { class: 'g-examples', 'data-testid': 'g-examples' },
    ...card.examples.map((e, i) => {
      const pl = e.pl ? h('p', { class: 'g-pl pl', 'data-testid': 'g-pl', hidden: true }, e.pl) : null;
      const bubble = h('div', { class: 'g-bubble' },
        h('p', { class: 'g-text sentence' }, ...e.parts.map((p) => (p.focus ? h('strong', { class: 'focus', 'data-testid': 'g-focus' }, p.text) : p.text))),
        pl,
      );
      if (pl) trackPlSlot(bubble, pl);
      return h('div', { class: 'g-example', 'data-testid': 'g-example', 'data-i': i },
        h('span', { class: 'g-pron', 'data-testid': 'g-pron' }, subjectChip(e.text, i)),
        bubble,
      );
    }));
  const toggle = translateButton(false, (on) => {
    for (const p of examples.querySelectorAll<HTMLElement>('.g-pl')) p.hidden = !on;
  }, ctx);

  // szybkie sprawdzenie: dwie odpowiedzi, jedna dobra; zła robi się czerwona, dobra zielona (nic nie wchodzi do wyniku)
  const opts = note.check.options.map((text) => {
    const o: HTMLButtonElement = h('button', { type: 'button', class: 'option', 'data-testid': 'g-option', onclick: () => pick(text, o) }, text);
    return o;
  });
  let solved = false;
  function pick(text: string, el: HTMLButtonElement): void {
    if (solved) return;
    if (text === note.check.answer) {
      solved = true;
      el.classList.add('right');
      for (const o of opts) o.disabled = true;
    } else {
      el.classList.add('wrong');
      el.disabled = true;
      shake(el);
    }
  }

  const el = h(
    'section',
    { class: 'task grammar exercise screen', 'data-task': 'grammar', 'data-testid': 'task-grammar', 'data-grammar': card.id },
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), T.chip), toggle),
    h('div', { class: 'ex-main screen-main g-card' },
      // dwie kolumny na szerokim ekranie (po lewej nauka, po prawej błąd, wskazówka i sprawdzenie), jedna na wąskim i na telefonie
      h('div', { class: 'g-col g-col-a' },
        h('h2', { class: 'g-title', 'data-testid': 'g-title' }, note.title),
        h('div', { class: 'g-rule', 'data-testid': 'g-rule' }, ...ruleLines(note.rule).map((line) => h('p', { class: 'g-rule-line' }, line))),
        examples,
        h('div', { class: 'g-formula', 'data-testid': 'g-formula' }, h('span', { class: 'g-label' }, tx(ctx, T.formula)), h('code', null, note.formula)),
      ),
      h('div', { class: 'g-col g-col-b' },
        h('div', { class: 'g-mistake', 'data-testid': 'g-mistake' },
          h('span', { class: 'g-label' }, tx(ctx, T.mistake)),
          h('p', { class: 'g-wrong' }, h('span', { class: 'g-mark', 'aria-hidden': 'true' }, icon('close')), h('span', null, note.mistake.wrong)),
          h('p', { class: 'g-right' }, h('span', { class: 'g-mark', 'aria-hidden': 'true' }, icon('check')), h('span', null, note.mistake.right)),
        ),
        h('div', { class: 'g-tip', 'data-testid': 'g-tip' }, h('span', { class: 'g-bulb', 'aria-hidden': 'true' }, icon('bulb')), h('p', null, note.tip)),
        h('div', { class: 'g-check', 'data-testid': 'g-check' },
          h('span', { class: 'g-label' }, tx(ctx, T.check)),
          h('p', { class: 'g-q sentence' }, note.check.q),
          h('div', { class: 'options' }, ...opts),
        ),
      ),
    ),
    h('div', { class: 'actions-bar single g-next' }, button([tx(ctx, L.next), icon('arrow')], () => env.done(null), { class: 'btn primary', testid: 'next' })),
  );
  const fit = fitToWindow(el, { grow: true });
  return { el, destroy: () => { fit.dispose(); env.ctx.player.stop(); } };
}
